import { useState } from "react";
import { nextDueDate } from "../components/shared";

// Shared guard for the three places a payment amount is freely typed in
// (Record Payment, Quick Pay, Edit Payment). capAmount is the tenant's rent
// plus that payment's late fee, or null when there's no known rent to check against.
function amountValidationError(rawAmount, capAmount) {
  const amount = parseFloat(rawAmount);
  if (rawAmount === "" || rawAmount === null || rawAmount === undefined || Number.isNaN(amount)) {
    return "Enter a valid amount.";
  }
  if (amount <= 0) {
    return "Amount must be greater than $0.";
  }
  if (capAmount != null && amount > capAmount) {
    return `Amount can't exceed rent + late fee ($${capAmount.toFixed(2)}).`;
  }
  return null;
}

export function usePaymentsData(supabase, landlordId, session, tenants, settings, reload) {
  const [payments, setPayments] = useState([]);
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [paymentEdits,     setPaymentEdits]     = useState([]);
  const [paymentAuditLog,  setPaymentAuditLog]  = useState([]);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [newPayment, setNewPayment] = useState({ tenant: "", property: "", amount: "", due: "", paid: "", status: "paid", payment_method: "Zelle", late_fee: "", notes: "", existingId: null });
  const [showEditPayment, setShowEditPayment] = useState(null);
  const [editPaymentDraft, setEditPaymentDraft] = useState({});
  const [newPaymentError, setNewPaymentError] = useState("");
  const [quickPayError, setQuickPayError] = useState("");
  const [editPaymentError, setEditPaymentError] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");
  const [expandedPaymentEdits, setExpandedPaymentEdits] = useState({});
  const [invoiceView, setInvoiceView] = useState("tenant");
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [sendVia, setSendVia] = useState({ email: true });

  // Single source of truth for invoice numbering: generates one via the
  // generate_invoice_number RPC and writes it, but only if this payment
  // doesn't already have one. Called from every path that can mark a
  // payment "paid" so a number is always assigned regardless of which
  // path caused the transition.
  const ensureInvoiceNumber = async (paymentId) => {
    const { data: existing } = await supabase.from("payments").select("invoice_number").eq("id", paymentId).maybeSingle();
    if (existing?.invoice_number) return existing.invoice_number;
    let invoiceNumber = null;
    try {
      const { data: invNum } = await supabase.rpc("generate_invoice_number", { p_user_id: landlordId });
      if (invNum) invoiceNumber = invNum;
    } catch (e) { /* RPC not yet set up */ }
    if (!invoiceNumber) return null;
    const { error } = await supabase.from("payments")
      .update({ invoice_number: invoiceNumber })
      .eq("id", paymentId)
      .is("invoice_number", null);
    if (error) { console.error("[ensureInvoiceNumber] update failed:", error.message); return null; }
    return invoiceNumber;
  };

  const addPayment = async () => {
    if (!newPayment.tenant) return;
    const tenant = tenants.find(t => t.name === newPayment.tenant);
    const lateFee = newPayment.late_fee ? parseFloat(newPayment.late_fee) : 0;
    const cap = tenant?.rent != null ? Number(tenant.rent) + (Number.isFinite(lateFee) ? lateFee : 0) : null;
    const amountErr = amountValidationError(newPayment.amount, cap);
    if (amountErr) { setNewPaymentError(amountErr); return; }
    setNewPaymentError("");
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();
    const day = String(settings.dueDayOfMonth || 1).padStart(2, "0");
    const defaultDue = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${day}`;
    const payload = {
      tenant: newPayment.tenant,
      property: newPayment.property || null,
      amount: newPayment.amount ? Math.round(parseFloat(newPayment.amount)) : null,
      due: newPayment.due || defaultDue,
      paid: newPayment.status === "paid" ? (newPayment.paid || today) : null,
      status: newPayment.status,
      payment_method: newPayment.payment_method || null,
      late_fee: newPayment.late_fee ? Math.round(parseFloat(newPayment.late_fee)) : null,
      notes: newPayment.notes || null,
    };
    setShowAddPayment(false);
    let paymentId = newPayment.existingId;
    if (newPayment.existingId) {
      const { error } = await supabase.from("payments").update(payload).eq("id", newPayment.existingId);
      if (error) { alert("Error: " + error.message); return; }
    } else {
      const { data, error } = await supabase.from("payments").insert([{ ...payload, user_id: landlordId }]).select().single();
      if (error) { alert("Error: " + error.message); return; }
      paymentId = data?.id;
    }
    if (newPayment.status === "paid" && paymentId) await ensureInvoiceNumber(paymentId);
    setNewPayment({ tenant: "", property: "", amount: "", due: "", paid: "", status: "paid", payment_method: "Zelle", late_fee: "", notes: "", existingId: null });
    reload();
  };

  const deletePayment = async (id) => {
    if (!window.confirm("Delete this payment record?")) return;
    await supabase.from("payments").delete().eq("id", id);
    reload();
  };

  const changePaymentStatus = async (paymentId, oldStatus, newStatus, reason) => {
    if (!reason?.trim()) return false;
    const today = new Date().toISOString().slice(0, 10);
    const update = { status: newStatus };
    if (newStatus === "paid" && !payments.find(p => p.id === paymentId)?.paid) update.paid = today;
    if (newStatus === "unpaid") {
      // Revert to a clean unpaid state — clear everything set when the payment was recorded.
      // Note: invoice_sent_at is intentionally omitted; it may not exist as a column in all
      // deployments and including an unknown column aborts the entire update.
      update.paid           = null;
      update.payment_method = null;
      update.invoice_number = null;
      update.late_fee       = null;
    }

    console.log("[changePaymentStatus] update payload:", update);
    const { error: upErr } = await supabase.from("payments").update(update).eq("id", paymentId);
    if (upErr) {
      console.error("[changePaymentStatus] update failed:", upErr.message, upErr.code);
      alert("Error updating payment: " + upErr.message);
      return false;
    }
    console.log("[changePaymentStatus] update succeeded");

    if (newStatus === "paid") await ensureInvoiceNumber(paymentId);

    await supabase.from("payment_audit_log").insert([{
      payment_id: paymentId,
      user_id:    landlordId,
      changed_by: session?.user?.id,
      old_status: oldStatus,
      new_status: newStatus,
      reason:     reason.trim(),
    }]);

    reload();
    return true;
  };

  const savePaymentEdit = async () => {
    const original = showEditPayment;
    const draft = editPaymentDraft;
    const notes = editInternalNotes.trim();
    const authResponse = await supabase.auth.getUser();
    const authEmail = authResponse?.data?.user?.email;
    const editedBy = authEmail || "unknown";
    const now = new Date().toISOString();

    console.log("[savePaymentEdit] authResponse email:", authEmail);
    console.log("[savePaymentEdit] editedBy:", editedBy);
    console.log("[savePaymentEdit] original.id:", original?.id, "type:", typeof original?.id);

    const trackFields = [
      { key: "amount",         label: "Amount" },
      { key: "due",            label: "Due Date" },
      { key: "payment_method", label: "Payment Method" },
      { key: "status",         label: "Status" },
      { key: "paid",           label: "Date Paid" },
    ];
    const changes = trackFields.filter(({ key }) => {
      const oldV = original[key] !== null && original[key] !== undefined ? String(original[key]) : "";
      const newV = draft[key] !== null && draft[key] !== undefined ? String(draft[key]) : "";
      const changed = oldV !== newV;
      if (changed) console.log(`[savePaymentEdit] changed field: ${key} | "${oldV}" → "${newV}"`);
      return changed;
    });

    if (changes.some(({ key }) => key === "amount")) {
      const tenant = tenants.find(t => t.name === original.tenant);
      const lateFee = original.late_fee != null ? Number(original.late_fee) : 0;
      const cap = tenant?.rent != null ? Number(tenant.rent) + (Number.isFinite(lateFee) ? lateFee : 0) : null;
      const amountErr = amountValidationError(draft.amount, cap);
      if (amountErr) { setEditPaymentError(amountErr); return; }
    }
    setEditPaymentError("");

    if (original.status === "paid" && changes.some(({ key }) => key === "due")) {
      const proceed = window.confirm("This invoice is already marked paid. Changing its due date will move this income to a different month in Financials reports. Continue?");
      if (!proceed) return;
    }

    if (changes.length === 0 && !notes) {
      console.log("[savePaymentEdit] no changes and no notes — closing without save");
      setShowEditPayment(null);
      return;
    }

    if (changes.length > 0) {
      const payload = {};
      changes.forEach(({ key }) => {
        payload[key] = key === "amount"
          ? (draft[key] !== "" && draft[key] != null ? Math.round(parseFloat(draft[key])) : null)
          : (draft[key] || null);
      });
      console.log("[savePaymentEdit] updating payments row:", payload);
      const { error: updateErr } = await supabase.from("payments").update(payload).eq("id", original.id);
      if (updateErr) { alert("Error saving edit: " + updateErr.message); return; }
    }

    if (changes.some(({ key }) => key === "status") && draft.status === "paid") {
      await ensureInvoiceNumber(original.id);
    }

    const editLogs = changes.map(({ key, label }) => ({
      payment_id: String(original.id),
      edited_by: editedBy,
      edited_at: now,
      field_changed: label,
      old_value: original[key] !== null && original[key] !== undefined ? String(original[key]) : null,
      new_value: draft[key] !== null && draft[key] !== undefined ? String(draft[key]) : null,
      internal_notes: notes || null,
    }));
    if (changes.length === 0 && notes) {
      editLogs.push({
        payment_id: String(original.id), edited_by: editedBy, edited_at: now,
        field_changed: "Note", old_value: null, new_value: null,
        internal_notes: notes,
      });
    }

    if (editLogs.length > 0) {
      console.log("[savePaymentEdit] FINAL INSERT PAYLOAD:", JSON.stringify(editLogs));
      console.log("[savePaymentEdit] payment_id value:", editLogs[0]?.payment_id, "| type:", typeof editLogs[0]?.payment_id);
      console.log("[savePaymentEdit] edited_by value:", editLogs[0]?.edited_by, "| type:", typeof editLogs[0]?.edited_by);
      const { data: insertData, error: insertErr } = await supabase.from("payment_edits").insert(editLogs).select();
      if (insertErr) {
        console.error("[savePaymentEdit] insert error:", insertErr);
        alert("Edit saved but audit log failed: " + insertErr.message + "\n\nCheck Supabase RLS policies on payment_edits.");
      } else {
        console.log("[savePaymentEdit] insert success:", insertData);
      }
    }

    setShowEditPayment(null);
    setEditInternalNotes("");
    reload();
  };

  const generateLeasePayments = async (tenant, ownerId) => {
    if (!tenant.name || !tenant.rent || tenant.is_complimentary || tenant.tenant_type === "non_paying") return;
    const dueDay = Math.max(1, Math.min(28, parseInt(tenant.due_day || settings.dueDayOfMonth || 1)));
    const today = new Date();
    const end = tenant.lease_end ? new Date(tenant.lease_end + "T12:00:00") : null;
    const records = [];
    let yr = today.getFullYear(), mo = today.getMonth();
    const maxMonths = end ? 600 : 12;
    for (let i = 0; i < maxMonths; i++) {
      const dueStr = `${yr}-${String(mo+1).padStart(2,"0")}-${String(dueDay).padStart(2,"0")}`;
      if (end && new Date(dueStr + "T12:00:00") > end) break;
      records.push({ tenant: tenant.name, property: tenant.address || tenant.property || null, amount: Math.round(parseFloat(tenant.rent)), due: dueStr, paid: null, status: "unpaid", payment_method: null, late_fee: null, notes: null, user_id: ownerId });
      mo++; if (mo > 11) { mo = 0; yr++; }
    }
    if (records.length === 0) return;
    const { data: existing } = await supabase.from("payments").select("due").eq("user_id", ownerId).eq("tenant", tenant.name);
    const existingDues = new Set((existing || []).map(e => e.due));
    const toInsert = records.filter(r => !existingDues.has(r.due));
    if (toInsert.length > 0) await supabase.from("payments").insert(toInsert);
  };

  const autoGeneratePayments = async () => {
    const active = tenants.filter(t => (t.status === "active" || t.status === "month-to-month") && !t.is_complimentary && t.tenant_type !== "non_paying");
    if (!window.confirm(`Generate unpaid rent records for ${active.length} active tenants for this month?`)) return;
    const now = new Date();
    const day = String(settings.dueDayOfMonth || 1).padStart(2, "0");
    const due = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${day}`;
    const { data: existing } = await supabase.from("payments").select("tenant, due").eq("user_id", landlordId);
    const existingKeys = new Set((existing || []).map(e => `${e.tenant}|${e.due}`));
    const records = active.filter(t => !existingKeys.has(`${t.name}|${due}`))
      .map(t => ({ tenant: t.name, property: t.address || t.property || null, amount: t.rent || null, due, paid: null, status: "unpaid", payment_method: null, late_fee: null, notes: null, user_id: landlordId }));
    if (records.length === 0) { alert("All active tenants already have a record for this month."); return; }
    const { error } = await supabase.from("payments").insert(records);
    if (error) { alert("Error: " + error.message); return; }
    reload();
  };

  const runLateFeeCheck = async () => {
    if (!settings.lateFeeEnabled) return;
    const today = new Date();
    const graceDays = parseInt(settings.lateFeeGraceDays) || 5;
    const feeAmount = parseInt(settings.lateFeeAmount) || 0;
    const candidates = payments.filter(p => {
      if (p.status === "paid") return false;
      if (!p.due) return false;
      const graceLine = new Date(p.due + "T12:00:00");
      graceLine.setDate(graceLine.getDate() + graceDays);
      return today > graceLine;
    });
    if (candidates.length === 0) return;
    await Promise.all(candidates.map(p => {
      const u = { status: "overdue" };
      if (feeAmount > 0 && !p.late_fee) u.late_fee = feeAmount;
      return supabase.from("payments").update(u).eq("id", p.id);
    }));
    reload();
  };

  const handleMarkPaid = async (payment) => {
    const today = new Date().toISOString().slice(0, 10);
    await supabase.from("payments").update({ status: "paid", paid: today }).eq("id", payment.id);
    const invoiceNumber = await ensureInvoiceNumber(payment.id);
    const updated = { ...payment, status: "paid", paid: today, invoice_number: invoiceNumber || payment.invoice_number || null };
    setInvoiceResult(null);
    setSendVia({ email: true });
    setInvoiceModal(updated);
    reload();
  };

  const quickCreatePayment = async (tenant, amount, method, datePaid) => {
    const cap = tenant?.rent != null ? Number(tenant.rent) : null;
    const amountErr = amountValidationError(amount, cap);
    if (amountErr) { setQuickPayError(amountErr); return false; }
    setQuickPayError("");
    const now = new Date();
    const day = String(settings.dueDayOfMonth || 1).padStart(2, "0");
    const due = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${day}`;
    const payload = {
      tenant: tenant.name,
      property: tenant.property || tenant.address || null,
      amount: amount ? Math.round(parseFloat(amount)) : null,
      due,
      paid: datePaid,
      status: "paid",
      payment_method: method || null,
      late_fee: null,
      notes: null,
      user_id: landlordId,
    };
    const { data, error } = await supabase.from("payments").insert([payload]).select().single();
    if (error) { alert("Error: " + error.message); return false; }
    const invoiceNumber = await ensureInvoiceNumber(data.id);
    setInvoiceResult(null);
    setSendVia({ email: true });
    setInvoiceModal({ ...data, invoice_number: invoiceNumber || data.invoice_number || null });
    reload();
    return true;
  };

  const openInvoice = (payment) => {
    setInvoiceResult(null);
    setSendVia({ email: true });
    setInvoiceModal(payment);
  };

  const sendInvoice = async () => {
    setInvoiceSending(true);
    const tenant = tenants.find(t => t.name === invoiceModal.tenant) || {};
    const paidDate = invoiceModal.paid;
    const nextDue = nextDueDate(paidDate);
    const results = [];

    const emailBody = `Dear ${invoiceModal.tenant},\n\nThis is your payment receipt for ${settings.propertyName}.\n\n${"─".repeat(40)}\nPAYMENT RECEIPT\n${"─".repeat(40)}\nProperty:     ${settings.propertyName}\nAddress:      ${tenant.address || settings.propertyAddress}${tenant.city ? ", " + tenant.city : ""}\nUnit:         ${invoiceModal.property || "—"}\nTenant:       ${invoiceModal.tenant}\n\nAmount Paid:  $${invoiceModal.amount?.toLocaleString()}\nPayment Date: ${paidDate}\nNext Due:     ${nextDue}\nLease Period: ${tenant.lease_start || "—"} → ${tenant.lease_end || "—"}\n${"─".repeat(40)}\n\nThank you for your payment!\n\n${settings.landlordName}\n${settings.landlordEmail} | ${settings.landlordPhone}`;

    if (sendVia.email && tenant.email) {
      try {
        const { data: fnData, error: fnErr } = await supabase.functions.invoke("send-invoice", {
          body: {
            payment:     invoiceModal,
            tenantEmail: tenant.email,
            tenantName:  tenant.name,
            nextDue,
            landlordId,
          },
        });
        let errMsg = fnErr?.message;
        if (fnErr) {
          try { const b = await fnErr.context?.json?.(); if (b?.error) errMsg = b.error; } catch {}
        }
        if (errMsg || fnData?.error) {
          results.push({ channel: "Email", success: false, msg: errMsg || fnData?.error });
        } else {
          results.push({ channel: "Email", success: true, msg: `Sent to ${tenant.email}` });
        }
      } catch (e) {
        results.push({ channel: "Email", success: false, msg: e.message });
      }
    }

    setInvoiceSending(false);
    setInvoiceResult(results);

    // Record when the invoice was sent so the payments list can show "Sent [date]"
    if (invoiceModal?.id && results.some(r => r.success)) {
      await supabase.from("payments")
        .update({ invoice_sent_at: new Date().toISOString() })
        .eq("id", invoiceModal.id);
      reload();
    }
  };

  return {
    payments, setPayments,
    collapsedMonths, setCollapsedMonths,
    showAddPayment, setShowAddPayment,
    newPayment, setNewPayment,
    newPaymentError, setNewPaymentError,
    quickPayError, setQuickPayError,
    showEditPayment, setShowEditPayment,
    editPaymentDraft, setEditPaymentDraft,
    editPaymentError, setEditPaymentError,
    editInternalNotes, setEditInternalNotes,
    expandedPaymentEdits, setExpandedPaymentEdits,
    invoiceView, setInvoiceView,
    invoiceModal, setInvoiceModal,
    invoiceSending, invoiceResult, setInvoiceResult,
    sendVia, setSendVia,
    paymentEdits, setPaymentEdits,
    paymentAuditLog, setPaymentAuditLog,
    addPayment, savePaymentEdit, deletePayment, changePaymentStatus,
    handleMarkPaid, quickCreatePayment, openInvoice, sendInvoice,
    autoGeneratePayments, runLateFeeCheck, generateLeasePayments,
  };
}
